import { FC, FocusEvent, ReactNode, SyntheticEvent } from "react";
import { FormikProps } from "formik";
import { useLingui } from "@lingui/react";
import get from "lodash-es/get";
import { Box } from "@otrium/core";
import { DropdownProps } from "semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown";
import { Checkbox } from "src/atoms/Checkbox";
import { Dropdown } from "src/atoms/DropDown";
import Input from "src/atoms/Input";
import { Label } from "src/atoms/Input/Input.styled";
import { Checkout } from "src/organisms/CheckoutView/CheckoutStepInfo";
import { useSegment } from "src/hooks/useSegment";
import { Profile } from "src/molecules/ProfileForm/ProfileForm";
import { I18n } from "@lingui/core";
import { PhoneInput } from "../PhoneInput";

export const getErrorMessage = (
  _: I18n["_"],
  error: string | undefined,
  touched: boolean | undefined,
  value: string | undefined
): string | ReactNode => {
  return !!error && (touched || (!touched && value)) ? <>{_(error)}</> : "";
};

const FieldWithError: FC<{
  type: "input" | "dropdown" | "checkbox" | "date" | "phone";
  formType: "profile" | "checkout";
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  disabled?: boolean;
  autoComplete?: string;
  label: string;
  field: string;
  options?: {
    key: string;
    text: string;
    value: string;
  }[];
}> = ({
  type,
  formType,
  formik,
  disabled = false,
  autoComplete,
  label,
  field,
  options = null,
}) => {
  const { _ } = useLingui();
  const { errors, handleBlur, touched, values } = formik;
  const { segmentFormTracking, segmentFormError } = useSegment();

  const value = get(values, field) as string | undefined;
  const errorMessage = get(errors, field) as string | undefined;
  const isTouched = get(touched, field) as boolean | undefined;

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    handleBlur(e);

    // TODO: This replicates previous behaviour of Profile form tracking
    // It's inconsistent with Checkout form tracking
    // Should be revisited to make it consistent
    let fieldName = field;
    let formName;
    if (formType === "profile") {
      fieldName = field.includes(".") ? field.split(".")[1] : field;
      formName = "Personal information";
    }

    void segmentFormTracking(fieldName, formName);
    if (errorMessage) {
      void segmentFormError(fieldName, errorMessage, formName);
    }
  };

  switch (type) {
    case "input":
    case "date":
      return (
        <Input
          type={type}
          label={get(values, field) ? label : ""}
          placeholder={label}
          disabled={disabled}
          errorMessage={getErrorMessage(_, errorMessage, isTouched, value)}
          {...formik.getFieldProps(field)}
          onBlur={onBlur}
          {...(autoComplete ? { autoComplete } : {})}
        />
      );
    case "dropdown":
      return (
        <div style={{ position: "relative" }}>
          {get(values, field) && <Label px={1}>{label}</Label>}
          <Dropdown
            id={field}
            value={value}
            onChange={(
              _event: SyntheticEvent<HTMLElement>,
              stateData: DropdownProps
            ) => formik.setFieldValue(field, stateData.value)}
            placeholder={label}
            disabled={disabled}
            options={options}
            errorMessage={getErrorMessage(_, errorMessage, isTouched, value)}
            onBlur={onBlur}
          />
        </div>
      );
    case "phone":
      return (
        <PhoneInput
          field={field}
          formik={formik}
          label={label}
          value={value}
          errorMessage={getErrorMessage(_, errorMessage, isTouched, value)}
        />
      );
    case "checkbox":
      const checkboxProps = formik.getFieldProps(field);
      return (
        <Checkbox {...checkboxProps} checked={checkboxProps.value as boolean}>
          <Box sx={{ whiteSpace: "initial" }}>{label}</Box>
        </Checkbox>
      );
  }
};

export default FieldWithError;
